import { useEffect, useState } from "react";
import { X, Download, BookOpen } from "lucide-react";

const DISMISS_KEY = "hw_install_dismissed_v1";
const DISMISS_DAYS = 14;

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    setVisible(false);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "dismissed") localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!visible || !deferredPrompt) return null;

  return (
    <div
      data-testid="install-prompt-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[92vw] max-w-md rounded-2xl border border-[#2A4B42]/20 bg-[#2A4B42] text-white shadow-2xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4 fade-in duration-500"
    >
      <div className="shrink-0 w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
        <BookOpen className="w-5 h-5 text-[#E9C46A]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-tight">Install Holy Words</p>
        <p className="text-xs text-white/70 leading-tight mt-0.5">Get the app on your device — faster, fullscreen, works offline.</p>
      </div>
      <button
        data-testid="install-prompt-install-button"
        onClick={install}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#E9C46A] text-[#2A4B42] text-xs font-bold px-3.5 py-2 hover:brightness-105 transition-[filter]"
      >
        <Download className="w-3.5 h-3.5" />
        Install
      </button>
      <button
        data-testid="install-prompt-dismiss-button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="shrink-0 p-1 rounded-full text-white/60 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
